/**
 * @fileoverview Component for generating issue intro and title options
 */

import { useState } from 'react';
import { SparklesIcon, CheckIcon, ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';

export default function IssueIntroGenerator({
  issueNumber,
  availableItems = [],
  newsSection = '',
  onIntroSelect,
  onTitleSelect,
  onGenerate,
  isGenerating = false,
  generatedIntro = null,
  generatedTitles = [],
  error = null,
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [selectedHighlights, setSelectedHighlights] = useState([]);
  const [selectedTitleIndex, setSelectedTitleIndex] = useState(null);

  const handleToggleHighlight = (itemHash) => {
    setSelectedHighlights((prev) => {
      if (prev.includes(itemHash)) {
        return prev.filter((h) => h !== itemHash);
      }
      if (prev.length >= 3) {
        // Replace the oldest selection
        return [...prev.slice(1), itemHash];
      }
      return [...prev, itemHash];
    });
  };

  const handleGenerate = () => {
    if (selectedHighlights.length !== 3) {
      return;
    }
    onGenerate(newsSection, selectedHighlights, issueNumber);
  };

  const handleSelectIntro = () => {
    if (generatedIntro && onIntroSelect) {
      onIntroSelect(generatedIntro);
    }
  };

  const handleSelectTitle = (index) => {
    setSelectedTitleIndex(index);
    if (generatedTitles[index] && onTitleSelect) {
      onTitleSelect(generatedTitles[index].title);
    }
  };

  const hasEnoughContent = newsSection.length >= 100;

  return (
    <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-200 dark:border-purple-800/50 overflow-hidden">
      {/* Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-purple-100 dark:hover:bg-purple-900/30 transition-colors"
      >
        <div className="flex items-center gap-2">
          <SparklesIcon className="h-5 w-5 text-purple-600 dark:text-purple-400" />
          <span className="font-medium text-purple-900 dark:text-purple-200">Issue Introduction Generator</span>
        </div>
        {isExpanded ? (
          <ChevronUpIcon className="h-5 w-5 text-purple-500 dark:text-purple-400" />
        ) : (
          <ChevronDownIcon className="h-5 w-5 text-purple-500 dark:text-purple-400" />
        )}
      </button>

      {isExpanded && (
        <div className="px-4 pb-4 space-y-4">
          <p className="text-xs text-purple-700 dark:text-purple-300">
            Select 3 items to highlight in the intro, then generate the &quot;In this issue...&quot; line and title options.
          </p>

          {/* Error State */}
          {error && (
            <div className="rounded-md bg-red-50 dark:bg-red-900/30 p-3">
              <p className="text-sm text-red-700 dark:text-red-200">{error}</p>
            </div>
          )}

          {/* Content requirement warning */}
          {!hasEnoughContent && (
            <div className="rounded-md bg-yellow-50 dark:bg-yellow-900/30 p-3">
              <p className="text-sm text-yellow-700 dark:text-yellow-200">
                The news section needs at least 100 characters of content to generate an intro.
                Current length: {newsSection.length} characters.
              </p>
            </div>
          )}

          {/* Item selection for highlights */}
          {!generatedIntro && (
            <div className="space-y-2">
              <label className="block text-xs font-medium text-purple-700 dark:text-purple-300">
                Select 3 items to highlight ({selectedHighlights.length}/3 selected)
              </label>
              <div className="max-h-48 overflow-y-auto space-y-1.5 bg-white dark:bg-gray-800 rounded-lg border border-purple-100 dark:border-purple-900/40 p-2">
                {availableItems.length === 0 ? (
                  <p className="text-xs text-gray-500 dark:text-gray-400 text-center py-4">
                    No items available. Add items to the issue first.
                  </p>
                ) : (
                  availableItems.map((item) => {
                    const isSelected = selectedHighlights.includes(item.hash);
                    const selectionIndex = selectedHighlights.indexOf(item.hash);

                    return (
                      <label
                        key={item.hash}
                        className={`flex items-start gap-2 text-xs p-2 rounded cursor-pointer transition-colors ${
                          isSelected ? 'bg-purple-100 dark:bg-purple-900/40' : 'hover:bg-gray-50 dark:hover:bg-gray-700'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleToggleHighlight(item.hash)}
                          disabled={isGenerating}
                          className="mt-0.5 h-3.5 w-3.5 rounded border-gray-300 dark:border-gray-600 text-purple-600 focus:ring-purple-500"
                        />
                        <div className="flex-1 min-w-0">
                          <span className="text-gray-700 dark:text-gray-200 line-clamp-1">
                            {item.title || 'Untitled'}
                          </span>
                        </div>
                        {isSelected && (
                          <span className="flex-shrink-0 w-5 h-5 flex items-center justify-center bg-purple-600 text-white text-[10px] font-bold rounded-full">
                            {selectionIndex + 1}
                          </span>
                        )}
                      </label>
                    );
                  })
                )}
              </div>

              <button
                onClick={handleGenerate}
                disabled={isGenerating || selectedHighlights.length !== 3 || !hasEnoughContent}
                className="flex items-center gap-2 px-3 py-2 text-sm bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed w-full justify-center"
              >
                {isGenerating ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                    Generating...
                  </>
                ) : (
                  <>
                    <SparklesIcon className="h-4 w-4" />
                    Generate Intro & Titles
                  </>
                )}
              </button>
            </div>
          )}

          {/* Generated Results */}
          {generatedIntro && (
            <div className="space-y-4">
              {/* Intro Line */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-medium text-purple-700 dark:text-purple-300">Generated Intro Line</label>
                  <button
                    onClick={handleSelectIntro}
                    className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-purple-600 dark:text-purple-300 bg-purple-100 dark:bg-purple-900/40 rounded hover:bg-purple-200 dark:hover:bg-purple-900/60"
                  >
                    <CheckIcon className="h-3.5 w-3.5" />
                    Use This Intro
                  </button>
                </div>
                <div className="p-3 bg-white dark:bg-gray-800 rounded-lg border border-purple-200 dark:border-purple-800/50">
                  <p className="text-sm text-gray-800 dark:text-gray-100">{generatedIntro}</p>
                </div>
              </div>

              {/* Title Options */}
              {generatedTitles.length > 0 && (
                <div className="space-y-2">
                  <label className="text-xs font-medium text-purple-700 dark:text-purple-300">Title Options</label>
                  <div className="space-y-2">
                    {generatedTitles.map((option, index) => (
                      <div
                        key={index}
                        className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                          selectedTitleIndex === index
                            ? 'bg-purple-100 dark:bg-purple-900/40 border-purple-400 dark:border-purple-600'
                            : 'bg-white dark:bg-gray-800 border-purple-200 dark:border-purple-800/50 hover:border-purple-300 dark:hover:border-purple-700'
                        }`}
                        onClick={() => handleSelectTitle(index)}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1">
                            <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{option.title}</p>
                            {option.rationale && (
                              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{option.rationale}</p>
                            )}
                          </div>
                          {selectedTitleIndex === index && (
                            <CheckIcon className="h-5 w-5 text-purple-600 dark:text-purple-400 flex-shrink-0" />
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Reset button */}
              <button
                onClick={() => {
                  setSelectedHighlights([]);
                  setSelectedTitleIndex(null);
                }}
                className="text-xs text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 underline"
              >
                Generate new intro with different highlights
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
